import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Maximize, DoorOpen, BedDouble, Bath, Calendar, Zap,
  Pencil, ChevronLeft, ChevronRight, User, Wallet, Home, FileText, Building2,
  Mail, Phone, X, Upload, Download, Trash2, Map as MapIcon, Loader2, ExternalLink,
  AlertTriangle, FileSignature, ClipboardList, PenLine, Copy, Image as ImageIcon, Sparkles, TrendingUp, Check,
} from "lucide-react";
import api from "../../lib/api";
import { SavedSignatureButton } from "../../components/dashboard/SavedSignatureButton";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);
const DOC_CATEGORIES = ["Bail", "Diagnostic", "DPE", "État des lieux", "Assurance", "Autre"];

function InfoRow({ icon: Icon, label, value }) {
  if (value === undefined || value === null || value === "" || value === 0) return null;
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="w-9 h-9 rounded-xl bg-brand-softer flex items-center justify-center text-brand-green"><Icon className="w-4 h-4" /></span>
      <div>
        <p className="text-xs text-brand-green/50">{label}</p>
        <p className="text-sm font-medium text-brand-greenDeep">{value}</p>
      </div>
    </div>
  );
}

export default function BienDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [rentEst, setRentEst] = useState(null);
  const [estBusy, setEstBusy] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantModal, setTenantModal] = useState(null);
  const [geo, setGeo] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docCategory, setDocCategory] = useState("Bail");
  const [docExpiry, setDocExpiry] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [genLease, setGenLease] = useState(false);
  const [leaseModal, setLeaseModal] = useState(false);
  const [leaseForm, setLeaseForm] = useState({ start_date: "", duration: "3 ans (renouvelable)", clauses: "" });
  const [genEtat, setGenEtat] = useState("");
  const [signature, setSignature] = useState(null);
  const [signLink, setSignLink] = useState("");
  const [creatingSign, setCreatingSign] = useState(false);
  const [remindLoading, setRemindLoading] = useState(false);
  const [ownerSignOpen, setOwnerSignOpen] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [ownerSaving, setOwnerSaving] = useState(false);
  const ownerCanvasRef = useRef(null);
  const ownerDrawing = useRef(false);
  const ownerHasDrawn = useRef(false);
  const [edlModal, setEdlModal] = useState(null); // {type, photos:[{url,label}]}
  const [edlUploading, setEdlUploading] = useState(false);
  const [edlGen, setEdlGen] = useState(false);
  const [edlSignLink, setEdlSignLink] = useState("");
  const [edlSigning, setEdlSigning] = useState(false);
  const edlRef = useRef(null);
  const docRef = useRef(null);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
    api.get("/receipts").then((r) => setReceipts(r.data)).catch(() => {});
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
    api.get(`/properties/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
    api.get(`/properties/${id}/lease-signature`).then((r) => setSignature(r.data && r.data.token ? r.data : null)).catch(() => {});
  }, [id]);

  const estimateRent = async () => {
    setEstBusy(true);
    try {
      const r = await api.post("/ai/estimate-rent", {
        property_type: p.type || "Appartement", surface: p.surface || 0, rooms: p.rooms || 0,
        bedrooms: p.bedrooms || 0, city: p.city || "", postal_code: p.postal_code || "",
        energy_class: p.energy_class || "", current_rent: p.rent || 0,
        furnished: (p.rental_type || "").toLowerCase().includes("meubl"),
      });
      setRentEst(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Estimation indisponible"); }
    finally { setEstBusy(false); }
  };

  const applyEstimatedRent = async () => {
    try {
      await api.put(`/properties/${id}`, { ...p, rent: rentEst.estimated_rent });
      const r = await api.get(`/properties/${id}`); setP(r.data);
      toast.success("Loyer mis à jour");
      setRentEst(null);
    } catch (e) { toast.error("Impossible d'appliquer le loyer"); }
  };

  useEffect(() => {
    if (!p) return;
    const q = [p.address, p.postal_code, p.city, p.country].filter(Boolean).join(" ");
    if (q.length < 3) return;
    api.get(`/geo/geocode?q=${encodeURIComponent(q)}`).then((r) => setGeo(r.data)).catch(() => {});
  }, [p]);

  if (notFound) {
    return (
      <div className="text-center py-20 text-brand-green/50">
        <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
        Bien introuvable.
        <div><button onClick={() => navigate("/app/biens")} className="mt-4 text-brand-greenDeep font-medium underline">Retour aux biens</button></div>
      </div>
    );
  }
  if (!p) return <div className="py-20 text-center text-brand-green/50">Chargement...</div>;

  const photos = (p.photos && p.photos.length ? p.photos : [p.image]).filter(Boolean);
  const propReceipts = receipts.filter((r) => r.property === p.name);
  const collected = propReceipts.filter((r) => r.status === "Payé").reduce((s, r) => s + (r.amount || 0), 0);
  const pending = propReceipts.filter((r) => r.status !== "Payé").reduce((s, r) => s + (r.amount || 0), 0);
  const addressFull = [p.address, p.address2, [p.postal_code, p.city].filter(Boolean).join(" "), p.region, p.country].filter(Boolean).join(", ");
  const isPassoire = ["F", "G"].includes((p.energy_class || "").toUpperCase());

  const daysUntil = (dstr) => {
    if (!dstr) return null;
    const d = new Date(dstr);
    if (isNaN(d)) return null;
    return Math.ceil((d - new Date()) / 86400000);
  };
  const docStatus = (dstr) => {
    const days = daysUntil(dstr);
    if (days === null) return null;
    if (days < 0) return { txt: "Expiré", cls: "bg-red-100 text-red-600" };
    if (days <= 60) return { txt: `Expire dans ${days} j`, cls: "bg-amber-100 text-amber-700" };
    return { txt: `Valide (${new Date(dstr).toLocaleDateString("fr-FR")})`, cls: "bg-brand-lime/30 text-brand-greenDeep" };
  };
  const expiringDocs = docs.filter((d) => { const n = daysUntil(d.expiry_date); return n !== null && n <= 60; });

  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIdx((i) => (i + 1) % photos.length);

  const openTenant = () => {
    if (!p.tenant) return;
    const t = tenants.find((x) => (x.name || "").toLowerCase() === p.tenant.toLowerCase());
    setTenantModal(t || { name: p.tenant });
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", docCategory);
      fd.append("label", file.name);
      if (docExpiry) fd.append("expiry_date", docExpiry);
      const res = await api.post(`/properties/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [res.data, ...d]);
      setDocExpiry("");
      toast.success("Document ajouté");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploadingDoc(false);
      if (docRef.current) docRef.current.value = "";
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.label} » ?`)) return;
    try {
      await api.delete(`/properties/documents/${doc.id}`);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success("Document supprimé");
    } catch { toast.error("Suppression impossible"); }
  };

  const downloadLease = async () => {
    setGenLease(true);
    try {
      const params = new URLSearchParams();
      if (leaseForm.start_date) params.set("start_date", leaseForm.start_date);
      if (leaseForm.duration) params.set("duration", leaseForm.duration);
      if (leaseForm.clauses) params.set("clauses", leaseForm.clauses);
      const res = await api.get(`/properties/${id}/lease-pdf?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `bail-${p.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setLeaseModal(false);
      toast.success("Bail généré");
    } catch { toast.error("Génération impossible"); }
    finally { setGenLease(false); }
  };

  const downloadEtat = async (type) => {
    setEdlModal({ type, photos: [] });
    setEdlSignLink("");
  };

  const createEdlSignature = async () => {
    setEdlSigning(true);
    try {
      const res = await api.post(`/properties/${id}/edl-signature`, { type: edlModal.type, photos: edlModal.photos });
      const link = `${window.location.origin}${res.data.url}`;
      setEdlSignLink(link);
      navigator.clipboard?.writeText(link);
      toast.success("Lien de signature de l'EDL créé (copié)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setEdlSigning(false); }
  };

  const addEdlPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setEdlUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/properties/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEdlModal((m) => ({ ...m, photos: [...m.photos, { url: res.data.url, label: "" }] }));
      }
    } catch { toast.error("Échec de l'upload"); }
    finally { setEdlUploading(false); if (edlRef.current) edlRef.current.value = ""; }
  };

  const generateEdl = async () => {
    setEdlGen(true);
    try {
      const res = await api.post(`/properties/${id}/etat-des-lieux-pdf`, { type: edlModal.type, photos: edlModal.photos }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `etat-des-lieux-${edlModal.type}-${p.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setEdlModal(null);
      toast.success("État des lieux généré");
    } catch { toast.error("Génération impossible"); }
    finally { setEdlGen(false); }
  };

  const remindTenant = async () => {
    setRemindLoading(true);
    try {
      await api.post(`/properties/${id}/lease-signature/remind`);
      toast.success("Rappel envoyé au locataire");
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible"); }
    finally { setRemindLoading(false); }
  };

  const ownerPos = (e) => {
    const c = ownerCanvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const ownerStart = (e) => { e.preventDefault(); ownerDrawing.current = true; const { x, y } = ownerPos(e); const ctx = ownerCanvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const ownerMove = (e) => { if (!ownerDrawing.current) return; e.preventDefault(); const { x, y } = ownerPos(e); const ctx = ownerCanvasRef.current.getContext("2d"); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#121311"; ctx.lineTo(x, y); ctx.stroke(); ownerHasDrawn.current = true; };
  const ownerEnd = () => { ownerDrawing.current = false; };
  const ownerClear = () => { const c = ownerCanvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); ownerHasDrawn.current = false; };

  const submitOwnerSign = async (savedName, savedUrl) => {
    const signer = (savedName || ownerName).trim();
    if (!signer) return toast.error("Saisissez votre nom");
    if (!savedUrl && !ownerHasDrawn.current) return toast.error("Dessinez votre signature");
    setOwnerSaving(true);
    try {
      const dataUrl = savedUrl || ownerCanvasRef.current.toDataURL("image/png");
      const res = await api.post(`/properties/${id}/owner-sign`, { signer_name: signer, signature: dataUrl });
      setSignature((s) => ({ ...(s || {}), owner_signed_at: res.data.owner_signed_at, owner_signer_name: signer }));
      setOwnerSignOpen(false);
      toast.success("Signature du bailleur enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Signature impossible"); }
    finally { setOwnerSaving(false); }
  };

  const createSignLink = async () => {
    setCreatingSign(true);
    try {
      const res = await api.post(`/properties/${id}/lease-signature`, {
        start_date: leaseForm.start_date, duration: leaseForm.duration, clauses: leaseForm.clauses,
      });
      const link = `${window.location.origin}/sign/${res.data.token}`;
      setSignLink(link);
      setSignature(res.data);
      try { await navigator.clipboard.writeText(link); toast.success("Lien copié dans le presse-papiers"); }
      catch { toast.success("Lien de signature créé"); }
    } catch { toast.error("Création impossible"); }
    finally { setCreatingSign(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/app/biens")} className="inline-flex items-center gap-2 text-brand-green/70 hover:text-brand-greenDeep font-medium" data-testid="back-to-biens">
          <ArrowLeft className="w-4 h-4" /> Retour aux biens
        </button>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={() => downloadEtat("entree")} disabled={!!genEtat} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-brand-soft text-brand-greenDeep font-semibold text-sm hover:bg-brand-softer disabled:opacity-60" data-testid="etat-entree-btn">
            {genEtat === "entree" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />} EDL entrée
          </button>
          <button onClick={() => downloadEtat("sortie")} disabled={!!genEtat} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-brand-soft text-brand-greenDeep font-semibold text-sm hover:bg-brand-softer disabled:opacity-60" data-testid="etat-sortie-btn">
            {genEtat === "sortie" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />} EDL sortie
          </button>
          <button onClick={() => setLeaseModal(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep text-brand-lime font-semibold text-sm" data-testid="generate-lease-btn">
            <FileSignature className="w-4 h-4" /> Générer le bail
          </button>
          <button onClick={() => navigate("/app/biens", { state: { editId: p.id } })} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm" data-testid="detail-edit-btn">
            <Pencil className="w-4 h-4" /> Modifier
          </button>
        </div>
      </div>

      {/* Alerts */}
      {isPassoire && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200" data-testid="dpe-alert">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Passoire thermique (DPE {p.energy_class})</p>
            <p className="text-sm text-red-600/90">Ce logement est énergivore. Depuis 2025, la location des biens classés G est encadrée (interdiction progressive F/G). Une rénovation énergétique est fortement recommandée.</p>
          </div>
        </div>
      )}
      {expiringDocs.length > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200" data-testid="doc-expiry-alert">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-700">Document(s) à renouveler</p>
            <p className="text-sm text-amber-700/90">{expiringDocs.map((d) => `${d.label} (${daysUntil(d.expiry_date) < 0 ? "expiré" : `dans ${daysUntil(d.expiry_date)} j`})`).join(" · ")}</p>
          </div>
        </div>
      )}

      {/* Gallery */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div className="relative rounded-2xl overflow-hidden bg-brand-softer aspect-[16/10]" data-testid="detail-gallery">
            <img src={imgSrc(photos[idx])} alt={p.name} className="w-full h-full object-cover" />
            {photos.length > 1 && (
              <>
                <button onClick={prev} data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-brand-greenDeep shadow"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={next} data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-brand-greenDeep shadow"><ChevronRight className="w-5 h-5" /></button>
                <span className="absolute bottom-3 right-3 text-xs px-2.5 py-1 rounded-full bg-black/60 text-white">{idx + 1} / {photos.length}</span>
              </>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {photos.map((url, i) => (
                <button key={url + i} onClick={() => setIdx(i)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${i === idx ? "border-brand-limeDark" : "border-transparent"}`}>
                  <img src={imgSrc(url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div>
            <span className="text-xs px-3 py-1 rounded-full font-medium bg-brand-softer text-brand-green">{p.type}</span>
            <h1 className="font-display text-3xl font-semibold text-brand-greenDeep mt-2">{p.name}</h1>
            <p className="flex items-center gap-1.5 text-sm text-brand-green/60 mt-1"><MapPin className="w-4 h-4" /> {addressFull}</p>
          </div>
          <div className="grid grid-cols-2 gap-4 p-4 rounded-2xl bg-white border border-brand-soft">
            <div><p className="text-xs text-brand-green/50">Loyer HC</p><p className="text-lg font-bold text-brand-greenDeep">{(p.rent || 0).toLocaleString("fr-FR")} €</p></div>
            <div><p className="text-xs text-brand-green/50">Charges</p><p className="text-lg font-bold text-brand-greenDeep">{(p.charges || 0).toLocaleString("fr-FR")} €</p></div>
            <div><p className="text-xs text-brand-green/50">Loyer CC</p><p className="text-lg font-bold text-brand-greenDeep">{((p.rent || 0) + (p.charges || 0)).toLocaleString("fr-FR")} €</p></div>
            <div><p className="text-xs text-brand-green/50">Dépôt</p><p className="text-lg font-bold text-brand-greenDeep">{(p.deposit || 0).toLocaleString("fr-FR")} €</p></div>
          </div>

          <div className="rounded-2xl border border-brand-soft bg-white p-4" data-testid="rent-ai-block">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><TrendingUp className="w-4 h-4" /></span>
                <div>
                  <p className="text-sm font-semibold text-brand-greenDeep">Estimation du loyer par IA</p>
                  <p className="text-xs text-brand-green/50">Loyer de marché estimé pour ce bien</p>
                </div>
              </div>
              <button onClick={estimateRent} disabled={estBusy} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="estimate-rent-btn">
                {estBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Estimer
              </button>
            </div>
            {rentEst && (
              <div className="mt-4 pt-4 border-t border-brand-soft" data-testid="rent-estimate-result">
                <div className="flex items-end gap-2 flex-wrap">
                  <span className="font-display text-3xl font-bold text-brand-greenDeep">{rentEst.estimated_rent.toLocaleString("fr-FR")} €</span>
                  <span className="text-sm text-brand-green/50 mb-1">/ mois HC</span>
                  <span className="ml-auto text-xs px-3 py-1 rounded-full bg-brand-softer text-brand-greenDeep font-semibold">Confiance : {rentEst.confidence}</span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm text-brand-green/70">
                  <span>Fourchette : <b>{rentEst.low.toLocaleString("fr-FR")} – {rentEst.high.toLocaleString("fr-FR")} €</b></span>
                  {rentEst.price_per_m2 > 0 && <span>• <b>{rentEst.price_per_m2} €/m²</b></span>}
                </div>
                {rentEst.rationale && <p className="text-sm text-brand-green/65 mt-3 leading-relaxed">{rentEst.rationale}</p>}
                {rentEst.factors?.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {rentEst.factors.map((f, i) => <li key={i} className="text-xs px-2.5 py-1 rounded-full bg-brand-softer text-brand-green/70">{f}</li>)}
                  </ul>
                )}
                <button onClick={applyEstimatedRent} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold" data-testid="apply-rent-btn">
                  <Check className="w-4 h-4" /> Appliquer ce loyer ({rentEst.estimated_rent.toLocaleString("fr-FR")} €)
                </button>
                <p className="text-[11px] text-brand-green/40 mt-2">Estimation IA à titre indicatif — vérifiez le marché local et l'encadrement des loyers.</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {p.rental_status && <span className="text-xs px-3 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep font-medium">{p.rental_status}</span>}
            {p.rental_type && <span className="text-xs px-3 py-1 rounded-full bg-brand-softer text-brand-green font-medium">{p.rental_type}</span>}
            {p.energy_class && <span className="text-xs px-3 py-1 rounded-full bg-brand-softer text-brand-green font-medium">DPE {p.energy_class}</span>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-brand-soft">
            <h3 className="font-semibold text-lg text-brand-greenDeep mb-2 flex items-center gap-2"><Home className="w-5 h-5" /> Caractéristiques</h3>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <InfoRow icon={Maximize} label="Superficie" value={p.surface ? `${p.surface} m²` : ""} />
              <InfoRow icon={DoorOpen} label="Pièces" value={p.rooms} />
              <InfoRow icon={BedDouble} label="Chambres" value={p.bedrooms} />
              <InfoRow icon={Bath} label="Salles de bain" value={p.bathrooms} />
              <InfoRow icon={Calendar} label="Date de construction" value={p.construction_date} />
              <InfoRow icon={Zap} label="Classe énergie" value={p.energy_class ? `DPE ${p.energy_class}` : ""} />
              <InfoRow icon={Zap} label="Expiration du DPE" value={p.dpe_expiry ? new Date(p.dpe_expiry).toLocaleDateString("fr-FR") : ""} />
              <InfoRow icon={User} label="Propriétaire" value={p.owner_name} />
            </div>
            {p.description && (
              <div className="mt-4 pt-4 border-t border-brand-soft">
                <p className="text-xs text-brand-green/50 mb-1">Description</p>
                <p className="text-sm text-brand-green/80 whitespace-pre-line">{p.description}</p>
              </div>
            )}
            {p.notes && (
              <div className="mt-4 pt-4 border-t border-brand-soft">
                <p className="text-xs text-brand-green/50 mb-1">Notes / Commentaires</p>
                <p className="text-sm text-brand-green/80 whitespace-pre-line">{p.notes}</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="detail-finances">
            <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Wallet className="w-5 h-5" /> Finances</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="rounded-xl bg-brand-lime/20 p-4"><p className="text-xs text-brand-green/60">Encaissé</p><p className="text-xl font-bold text-brand-greenDeep">{collected.toLocaleString("fr-FR")} €</p></div>
              <div className="rounded-xl bg-amber-50 p-4"><p className="text-xs text-brand-green/60">En attente</p><p className="text-xl font-bold text-amber-600">{pending.toLocaleString("fr-FR")} €</p></div>
            </div>
            <div className="space-y-2">
              {propReceipts.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-brand-soft last:border-0 text-sm">
                  <span className="flex items-center gap-2 text-brand-green/70"><FileText className="w-4 h-4" /> {r.month}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-brand-greenDeep">{(r.amount || 0).toLocaleString("fr-FR")} €</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${r.status === "Payé" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                  </div>
                </div>
              ))}
              {propReceipts.length === 0 && <p className="text-sm text-brand-green/50">Aucune quittance pour ce bien.</p>}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="detail-tenant">
            <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><User className="w-5 h-5" /> Locataire</h3>
            {p.tenant ? (
              <button onClick={openTenant} className="w-full flex items-center gap-3 text-left hover:bg-brand-softer rounded-xl p-2 -m-2 transition-colors" data-testid="tenant-link">
                <span className="w-11 h-11 rounded-full bg-brand-lime flex items-center justify-center font-semibold text-brand-greenDeep">{p.tenant.split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
                <div className="flex-1"><p className="text-sm font-medium text-brand-greenDeep">{p.tenant}</p><p className="text-xs text-brand-green/50">{p.rental_status === "Loué" ? "Bail en cours" : p.rental_status}</p></div>
                <ChevronRight className="w-4 h-4 text-brand-green/40" />
              </button>
            ) : (
              <p className="text-sm text-brand-green/50">Aucun locataire pour le moment.</p>
            )}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="detail-map">
            <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><MapIcon className="w-5 h-5" /> Localisation</h3>
            {geo && geo.lat && geo.lon ? (
              <div className="space-y-2">
                <div className="rounded-xl overflow-hidden border border-brand-soft">
                  <iframe
                    title="Localisation du bien"
                    data-testid="map-iframe"
                    width="100%" height="220" loading="lazy" style={{ border: 0 }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.lon - 0.006}%2C${geo.lat - 0.003}%2C${geo.lon + 0.006}%2C${geo.lat + 0.003}&layer=mapnik&marker=${geo.lat}%2C${geo.lon}`}
                  />
                </div>
                <a href={`https://www.openstreetmap.org/?mlat=${geo.lat}&mlon=${geo.lon}#map=17/${geo.lat}/${geo.lon}`} target="_blank" rel="noreferrer" className="text-xs text-brand-greenDeep font-medium inline-flex items-center gap-1 hover:underline">
                  Ouvrir dans OpenStreetMap <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ) : (
              <p className="text-sm text-brand-green/50">Adresse non localisée (France uniquement).</p>
            )}
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="detail-documents">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2"><FileText className="w-5 h-5" /> Documents ({docs.length})</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={docExpiry} onChange={(e) => setDocExpiry(e.target.value)} title="Date d'expiration (diagnostics, DPE...)" className="px-3 py-2 rounded-xl border border-brand-soft bg-white text-sm outline-none" data-testid="doc-expiry-input" />
            <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} className="px-3 py-2 rounded-xl border border-brand-soft bg-white text-sm outline-none" data-testid="doc-category-select">
              {DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button onClick={() => docRef.current?.click()} disabled={uploadingDoc} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="upload-doc-btn">
              {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Ajouter
            </button>
            <input ref={docRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden" onChange={handleDocUpload} data-testid="doc-file-input" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-brand-soft" data-testid={`doc-${d.id}`}>
              <span className="w-10 h-10 rounded-lg bg-brand-softer flex items-center justify-center text-brand-green shrink-0"><FileText className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-greenDeep truncate">{d.label}</p>
                <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep font-semibold">{d.category}</span>
                  {docStatus(d.expiry_date) && <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${docStatus(d.expiry_date).cls}`}>{docStatus(d.expiry_date).txt}</span>}
                </div>
              </div>
              <a href={imgSrc(d.url)} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-brand-softer flex items-center justify-center text-brand-greenDeep hover:bg-brand-soft" title="Ouvrir"><Download className="w-4 h-4" /></a>
              <button onClick={() => deleteDoc(d)} className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-100" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {docs.length === 0 && <p className="text-sm text-brand-green/50 col-span-full">Aucun document. Ajoutez le bail, les diagnostics, le DPE…</p>}
        </div>
      </div>

      {/* Signature status */}
      <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="signature-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2"><PenLine className="w-5 h-5" /> Signature du bail en ligne</h3>
          {signature && signature.status === "signed" ? (
            <span className="text-xs px-3 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep font-semibold" data-testid="sign-status">Signé le {signature.signed_at} par {signature.signer_name}</span>
          ) : signature && signature.status === "pending" ? (
            <span className="text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold" data-testid="sign-status">En attente de signature</span>
          ) : (
            <span className="text-xs px-3 py-1 rounded-full bg-brand-softer text-brand-green/60 font-semibold" data-testid="sign-status">Non initiée</span>
          )}
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {signature && signature.token && (
            <button onClick={() => { const l = `${window.location.origin}/sign/${signature.token}`; setSignLink(l); navigator.clipboard?.writeText(l); toast.success("Lien copié"); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-soft text-brand-greenDeep font-medium text-sm hover:bg-brand-softer" data-testid="copy-sign-link-btn">
              <Copy className="w-4 h-4" /> Copier le lien
            </button>
          )}
          {signature && signature.status === "signed" && (
            <a href={`${BACKEND_URL}/api/sign/${signature.token}/lease-pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep text-brand-lime font-medium text-sm" data-testid="download-signed-lease-btn">
              <Download className="w-4 h-4" /> Bail signé (PDF)
            </a>
          )}
          {(!signature || signature.status !== "signed") && (
            <button onClick={() => setLeaseModal(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-medium text-sm" data-testid="init-sign-btn">
              <FileSignature className="w-4 h-4" /> {signature ? "Renouveler le lien" : "Envoyer pour signature"}
            </button>
          )}
          {signature && signature.status === "pending" && (
            <button onClick={remindTenant} disabled={remindLoading} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-soft text-brand-greenDeep font-medium text-sm hover:bg-brand-softer disabled:opacity-60" data-testid="remind-tenant-btn">
              {remindLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Relancer le locataire
            </button>
          )}
          {signature && signature.token && (
            signature.owner_signed_at ? (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-brand-lime/30 text-brand-greenDeep font-semibold" data-testid="owner-signed-status"><Check className="w-3.5 h-3.5" /> Bailleur signé le {signature.owner_signed_at}</span>
            ) : (
              <button onClick={() => setOwnerSignOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-soft text-brand-greenDeep font-medium text-sm hover:bg-brand-softer" data-testid="owner-sign-btn">
                <PenLine className="w-4 h-4" /> Signer (bailleur)
              </button>
            )
          )}
        </div>
      </div>

      {/* EDL modal */}
      {edlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEdlModal(null)} data-testid="edl-modal">
          <div className="bg-white rounded-3xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">État des lieux d'{edlModal.type === "sortie" ? "sortie" : "entrée"}</h3>
              <button onClick={() => setEdlModal(null)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-brand-green/60 mb-4">Ajoutez des photos pour documenter l'état de chaque pièce (optionnel), puis générez le PDF.</p>
            <div className="flex flex-wrap gap-3 mb-4">
              {edlModal.photos.map((ph, i) => (
                <div key={i} className="w-28 rounded-xl border border-brand-soft overflow-hidden" data-testid={`edl-photo-${i}`}>
                  <img src={imgSrc(ph.url)} alt="" className="w-28 h-20 object-cover" />
                  <input value={ph.label} onChange={(e) => setEdlModal((m) => ({ ...m, photos: m.photos.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} placeholder="Pièce..." className="w-full px-2 py-1 text-xs border-t border-brand-soft outline-none" />
                </div>
              ))}
              <button onClick={() => edlRef.current?.click()} disabled={edlUploading} className="w-28 h-[104px] rounded-xl border-2 border-dashed border-brand-soft flex flex-col items-center justify-center text-brand-green/50 hover:border-brand-limeDark" data-testid="edl-add-photo-btn">
                {edlUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImageIcon className="w-5 h-5 mb-1" /><span className="text-xs">Photo</span></>}
              </button>
              <input ref={edlRef} type="file" accept="image/*" multiple className="hidden" onChange={addEdlPhotos} data-testid="edl-file-input" />
            </div>
            <button onClick={generateEdl} disabled={edlGen} className="w-full py-3 rounded-full bg-brand-greenDeep text-brand-lime font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="edl-generate-btn">
              {edlGen ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />} Générer le PDF
            </button>
            {edlModal.type === "sortie" && (
              <div className="mt-3 pt-3 border-t border-brand-soft">
                {!edlSignLink ? (
                  <button onClick={createEdlSignature} disabled={edlSigning} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="edl-sign-btn">
                    {edlSigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Faire signer par le locataire
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input readOnly value={edlSignLink} className="flex-1 px-3 py-2 rounded-xl border border-brand-soft text-sm bg-brand-softer" data-testid="edl-sign-link" />
                      <button onClick={() => { navigator.clipboard?.writeText(edlSignLink); toast.success("Lien copié"); }} className="px-3 rounded-xl border border-brand-soft hover:bg-brand-softer"><Copy className="w-4 h-4" /></button>
                    </div>
                    <a href={edlSignLink} target="_blank" rel="noreferrer" className="w-full py-2.5 rounded-full bg-brand-greenDeep text-brand-lime font-semibold flex items-center justify-center gap-2" data-testid="edl-open-sign-btn">
                      <ExternalLink className="w-4 h-4" /> Ouvrir la page de signature
                    </a>
                  </div>
                )}
                <p className="text-xs text-brand-green/45 mt-2">Le locataire signe en ligne · document horodaté et scellé.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Owner signature modal */}
      {ownerSignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setOwnerSignOpen(false)} data-testid="owner-sign-modal">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">Signature du bailleur</h3>
              <button onClick={() => setOwnerSignOpen(false)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <SavedSignatureButton propertyId={id} onUse={(n, d) => submitOwnerSign(n, d)} busy={ownerSaving} />
            <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Votre nom</label>
            <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder={p.owner_name || "Nom du bailleur"} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark mb-4" data-testid="owner-name-input" />
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-brand-green/70">Signature</label>
              <button onClick={ownerClear} className="text-xs text-brand-green/60 hover:text-brand-greenDeep">Effacer</button>
            </div>
            <canvas ref={ownerCanvasRef} width={520} height={170} data-testid="owner-signature-canvas"
              className="w-full h-40 rounded-2xl border-2 border-dashed border-brand-soft bg-white touch-none cursor-crosshair"
              onMouseDown={ownerStart} onMouseMove={ownerMove} onMouseUp={ownerEnd} onMouseLeave={ownerEnd}
              onTouchStart={ownerStart} onTouchMove={ownerMove} onTouchEnd={ownerEnd} />
            <button onClick={() => submitOwnerSign()} disabled={ownerSaving} className="w-full mt-4 py-3 rounded-full bg-brand-greenDeep text-brand-lime font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="owner-sign-submit">
              {ownerSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Enregistrer ma signature
            </button>
          </div>
        </div>
      )}

      {/* Lease customization modal */}
      {leaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setLeaseModal(false)} data-testid="lease-modal">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">Générer le bail</h3>
              <button onClick={() => setLeaseModal(false)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Date de prise d'effet</label>
                <input type="date" value={leaseForm.start_date} onChange={(e) => setLeaseForm({ ...leaseForm, start_date: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark" data-testid="lease-start-date" />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Durée du bail</label>
                <input value={leaseForm.duration} onChange={(e) => setLeaseForm({ ...leaseForm, duration: e.target.value })} placeholder="Ex : 3 ans (renouvelable)" className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark" data-testid="lease-duration" />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Clauses particulières (une par ligne)</label>
                <textarea rows={4} value={leaseForm.clauses} onChange={(e) => setLeaseForm({ ...leaseForm, clauses: e.target.value })} placeholder={"Ex : Interdiction de sous-louer\nAnimaux non autorisés\nAssurance PNO obligatoire"} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark resize-none" data-testid="lease-clauses" />
              </div>
              <button onClick={downloadLease} disabled={genLease} className="w-full py-3 rounded-full bg-brand-greenDeep text-brand-lime font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="lease-generate-confirm">
                {genLease ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Télécharger le PDF
              </button>
              <button onClick={createSignLink} disabled={creatingSign} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="create-sign-link-btn">
                {creatingSign ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Créer un lien de signature en ligne
              </button>
              {signLink && (
                <div className="p-3 rounded-xl bg-brand-softer text-xs break-all" data-testid="sign-link-box">
                  <p className="text-brand-green/60 mb-1">Lien à envoyer au locataire :</p>
                  <a href={signLink} target="_blank" rel="noreferrer" className="text-brand-greenDeep font-medium underline">{signLink}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tenant profile modal */}
      {tenantModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setTenantModal(null)} data-testid="tenant-modal">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">Profil locataire</h3>
              <button onClick={() => setTenantModal(null)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 mb-5">
              <span className="w-16 h-16 rounded-2xl bg-brand-lime flex items-center justify-center font-semibold text-xl text-brand-greenDeep">{tenantModal.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
              <div>
                <p className="text-lg font-semibold text-brand-greenDeep">{tenantModal.name}</p>
                {tenantModal.status && <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${tenantModal.status === "À jour" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-red-100 text-red-600"}`}>{tenantModal.status}</span>}
              </div>
            </div>
            {tenantModal.id ? (
              <div className="space-y-3 text-sm text-brand-green/75">
                {tenantModal.email && <p className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-brand-green/40" /> {tenantModal.email}</p>}
                <p className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-brand-green/40" /> {tenantModal.phone || "—"}</p>
                <p className="flex items-center gap-2.5"><Home className="w-4 h-4 text-brand-green/40" /> {tenantModal.property || p.name}</p>
                <p className="flex items-center gap-2.5"><Calendar className="w-4 h-4 text-brand-green/40" /> Depuis le {tenantModal.since ? new Date(tenantModal.since).toLocaleDateString("fr-FR") : "—"}</p>
                <button onClick={() => navigate("/app/locataires")} className="w-full mt-3 py-2.5 rounded-full bg-brand-softer text-brand-greenDeep font-medium hover:bg-brand-soft transition-colors">Voir tous les locataires</button>
              </div>
            ) : (
              <p className="text-sm text-brand-green/50">Ce locataire n'est pas encore enregistré dans votre liste. <button onClick={() => navigate("/app/locataires")} className="text-brand-greenDeep underline font-medium">Ajouter</button></p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
